import { Node, escapeForRegEx, mergeAttributes } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import Suggestion from "@tiptap/suggestion";

const MentionPluginKey = new PluginKey("mention");
const Mention = Node.create({
    name: "mention",
    group: "inline",
    inline: true,
    selectable: false,
    atom: true,
    addOptions() {
        return {
            renderLabel({ options, node }) {
                return `${options.suggestion.char}${node.attrs.label}`;
            },
            suggestion: {
                char: "@",
                allowSpaces: false,
                pluginKey: MentionPluginKey,
                findSuggestionMatch: findSuggestionMatch,
                command: ({ editor, range, props }) => {
                    editor
                        .chain()
                        .focus()
                        .insertContentAt(range, [
                            {
                                type: this.name,
                                attrs: {
                                    id: props.id,
                                    label: generateLabel(props),
                                    username: props.username,
                                    slackWorkspaceId: props.workspaceId,
                                    slackUserId: props.slackUserId,
                                    isMember: props.isMember,
                                    role: props.role,
                                },
                            },
                            {
                                type: "text",
                                text: " ",
                            },
                        ])
                        .run();
                },
            },
        };
    },
    addAttributes() {
        return {
            id: {
                parseHTML: (element) => element.getAttribute("data-id"),
                renderHTML: (attributes) => {
                    return {
                        "data-id": attributes.id,
                    };
                },
            },
            label: {
                parseHTML: (element) => element.getAttribute("data-label"),
                renderHTML: (attributes) => {
                    return {
                        "data-label": attributes.label,
                    };
                },
            },
            username: {
                parseHTML: (element) => element.getAttribute("data-username"),
                renderHTML: (attributes) => {
                    return {
                        "data-username": attributes.username,
                    };
                },
            },
            isMember: {
                parseHTML: (element) => element.getAttribute("data-ismember"),
                renderHTML: (attributes) => {
                    return {
                        "data-ismember": attributes.isMember,
                    };
                },
            },
            slackWorkspaceId: {
                parseHTML: (element) =>
                    element.getAttribute("data-slackWorkspaceId"),
                renderHTML: (attributes) => {
                    return {
                        "data-slackWorkspaceId": attributes.slackWorkspaceId,
                    };
                },
            },
            slackUserId: {
                parseHTML: (element) =>
                    element.getAttribute("data-slackUserId"),
                renderHTML: (attributes) => {
                    return {
                        "data-slackUserId": attributes.slackUserId,
                    };
                },
            },
            role: {
                parseHTML: (element) => element.getAttribute("data-role"),
                renderHTML: (attributes) => {
                    return {
                        "data-role": attributes.role,
                    };
                },
            },
        };
    },
    parseHTML() {
        return [{ tag: `cw-mention[data-type="${this.name}"]` }];
    },
    renderHTML({ node, HTMLAttributes }) {
        return [
            "cw-mention",
            mergeAttributes({ "data-type": this.name }, HTMLAttributes),
            this.options.renderLabel({
                options: this.options,
                node,
            }),
        ];
    },
    addKeyboardShortcuts() {
        return {
            //on backspace delete text and open suggestion menu
            Backspace: () =>
                this.editor.commands.command(({ tr, state }) => {
                    let isMention = false;
                    const { selection } = state;
                    const { empty, anchor } = selection;

                    if (!empty) {
                        return false;
                    }

                    state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
                        if (node.type.name === this.name) {
                            isMention = true;
                            tr.insertText(
                                this.options.suggestion.char,
                                pos,
                                pos + node.nodeSize,
                            );
                            return false;
                        }
                    });

                    return isMention;
                }),
        };
    },
    addProseMirrorPlugins() {
        return [
            Suggestion({
                editor: this.editor,
                ...this.options.suggestion,
            }),
        ];
    },
});

//This funtion determines when the mention suggestion list will appear, a modified function is needed so suggestion list doesn't close when the user types the
//second @ character that is part of the email, original function can be found here: https://github.com/ueberdosis/tiptap/blob/main/packages/suggestion/src/findSuggestionMatch.ts
function findSuggestionMatch(config) {
    const { char, allowSpaces, allowedPrefixes, startOfLine, $position } =
        config;

    const escapedChar = escapeForRegEx(char);
    const suffix = new RegExp(`\\s${escapedChar}$`);
    const prefix = startOfLine ? "^" : "";
    const regexp = allowSpaces
        ? new RegExp(`${prefix}${escapedChar}.*?(?=\\s|$)`, "gm")
        : new RegExp(`${prefix}(?:^)?${escapedChar}[^\\s]*`, "gm");

    const text = $position.nodeBefore?.isText && $position.nodeBefore.text;

    if (!text) {
        return null;
    }

    const textFrom = $position.pos - text.length;
    const match = Array.from(text.matchAll(regexp)).pop();

    if (!match || match.input === undefined || match.index === undefined) {
        return null;
    }

    // JavaScript doesn't have lookbehinds. This hacks a check that first character
    // is a space or the start of the line
    const matchPrefix = match.input.slice(
        Math.max(0, match.index - 1),
        match.index,
    );
    const matchPrefixIsAllowed = new RegExp(
        `^[${allowedPrefixes?.join("")}\0]?$`,
    ).test(matchPrefix);

    if (allowedPrefixes !== null && !matchPrefixIsAllowed) {
        return null;
    }

    // The absolute position of the match in the document
    const from = textFrom + match.index;
    let to = from + match[0].length;

    // Edge case handling; if spaces are allowed and we're directly in between
    // two triggers
    if (allowSpaces && suffix.test(text.slice(to - 1, to + 1))) {
        match[0] += " ";
        to += 1;
    }

    // If the $position is located within the matched substring, return that range
    if (from < $position.pos && to >= $position.pos) {
        return {
            range: {
                from,
                to,
            },
            query: match[0].slice(char.length),
            text: match[0],
        };
    }

    return null;
}

const generateLabel = (props) => {
    if (
        (props.firstName && props.firstName.trim() !== "") ||
        (props.lastName && props.lastName.trim())
    ) {
        return props.firstName.trim() + " " + props.lastName;
    } else {
        return props.username;
    }
};

export { Mention };
